import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAppWorkspace } from "@/contexts/AppWorkspaceContext";
import { useMutation } from "convex/react";
import { api } from "@/convex/src/_generated/api";
import { toast } from "sonner";

/**
 * BillingSection
 *
 * Billing information and status
 * Only visible to workspace owners
 */
export function BillingSection() {
  const { activeWorkspace, userRole } = useAppWorkspace();

  const [billingEmail, setBillingEmail] = useState(
    activeWorkspace?.billingEmail || "",
  );
  const [isSaving, setIsSaving] = useState(false);

  const updateBillingMutation = useMutation(api.workspaces.updateBillingStatus);

  if (!activeWorkspace) {
    return <div>No workspace selected</div>;
  }

  const canManageBilling = userRole === "owner";

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateBillingMutation({
        workspaceId: activeWorkspace._id,
        billingEmail: billingEmail.trim() || undefined,
      });
      toast.success("Billing information updated");
    } catch (error) {
      console.error("Failed to update billing:", error);
      toast.error("Failed to update billing information");
    } finally {
      setIsSaving(false);
    }
  };

  const getBillingStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300";
      case "trial":
        return "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300";
      case "past_due":
        return "bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-300";
      case "canceled":
        return "bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300";
      default:
        return "bg-gray-50 dark:bg-gray-950 text-gray-700 dark:text-gray-300";
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold mb-6">Billing</h1>

      {!canManageBilling && (
        <div className="bg-muted/30 border border-border rounded-lg p-4 mb-6 max-w-2xl">
          <p className="text-sm text-muted-foreground">
            You don't have permission to manage billing. Contact the workspace
            owner to update billing information or manage subscriptions.
          </p>
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold mb-4">Billing Information</h3>

        <div className="space-y-4 max-w-xl">
          <div className="space-y-2">
            <Label>Current Status</Label>
            <div>
              <Badge
                className={
                  getBillingStatusColor(activeWorkspace.billingStatus) +
                  " capitalize"
                }
              >
                {activeWorkspace.billingStatus}
              </Badge>
            </div>
            {activeWorkspace.billingStatus === "trial" && (
              <p className="text-xs text-muted-foreground">
                You're currently on a trial plan with limited features.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="billing-email">Billing Email</Label>
            <Input
              id="billing-email"
              type="email"
              value={billingEmail}
              onChange={(e) => setBillingEmail(e.target.value)}
              placeholder="billing@example.com"
              disabled={!canManageBilling}
              className={!canManageBilling ? "bg-muted cursor-not-allowed" : ""}
            />
            <p className="text-xs text-muted-foreground">
              Billing notifications will be sent to this email
            </p>
            {canManageBilling && (
              <Button
                onClick={handleSave}
                disabled={
                  isSaving || billingEmail === activeWorkspace.billingEmail
                }
                className="mt-2"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h4 className="text-base font-semibold mb-2">Payment Method</h4>
        <p className="text-sm text-muted-foreground mb-4">
          Payment integration coming soon. Contact support for enterprise plans.
        </p>
        <Button variant="outline" disabled>
          Manage Payment Method
        </Button>
      </div>

      <div className="border-t pt-6">
        <h4 className="text-base font-semibold mb-2">Subscription Plan</h4>
        <p className="text-sm text-muted-foreground mb-4">
          Upgrade your plan to access more features and higher usage limits.
        </p>
        <Button variant="outline" disabled>
          Upgrade Plan
        </Button>
      </div>
    </div>
  );
}
